import React, { useState, useEffect, useCallback } from 'react';
import { api } from '@/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  AlertTriangle, Scale, Gavel, Clock, Handshake,
  CheckSquare, ShieldAlert, Calendar, TrendingUp, Target
} from 'lucide-react';

const KPI_CONFIG = [
  { key: 'potential_disputes', label: 'Potential', icon: AlertTriangle, color: 'text-amber-600' },
  { key: 'formal_disputes', label: 'Formal', icon: Scale, color: 'text-red-600' },
  { key: 'negotiation_active', label: 'Negotiation', icon: Handshake, color: 'text-blue-600' },
  { key: 'adjudications_live', label: 'Adjudications', icon: Gavel, color: 'text-purple-600' },
  { key: 'dab_matters_live', label: 'DAB / DAAB', icon: ShieldAlert, color: 'text-indigo-600' },
  { key: 'arbitrations_live', label: 'Arbitrations', icon: Scale, color: 'text-rose-600' },
  { key: 'awaiting_decision', label: 'Awaiting Decision', icon: Clock, color: 'text-cyan-600' },
  { key: 'settlement_opportunities', label: 'Settlement Opps', icon: Target, color: 'text-green-600' },
  { key: 'time_bar_risks', label: 'Time-Bar Risks', icon: AlertTriangle, color: 'text-red-700' },
];

const RISK_LABELS = {
  no_owner_assigned: 'No owner assigned',
  deadline_missed: 'Deadline missed',
  time_bar_risk: 'Time-bar risk',
  evidence_incomplete: 'Evidence incomplete',
  no_issues_framed: 'No issues framed',
};

const STATUS_COLORS = {
  potential_dispute: 'bg-amber-100 text-amber-700',
  internal_escalation: 'bg-orange-100 text-orange-700',
  negotiation_active: 'bg-blue-100 text-blue-700',
  formal_dispute: 'bg-red-100 text-red-700',
  referral_in_preparation: 'bg-purple-100 text-purple-700',
  forum_active: 'bg-indigo-100 text-indigo-700',
  awaiting_decision: 'bg-cyan-100 text-cyan-700',
  decision_issued: 'bg-teal-100 text-teal-700',
  compliance_pending: 'bg-amber-100 text-amber-700',
  settled: 'bg-green-100 text-green-700',
  closed: 'bg-slate-100 text-slate-600',
};

export default function DisputeDashboard({ projectId, settings, onNavigate, onOpenMatter }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.disputes.getDashboard(projectId);
        setData(res.data);
      } catch { /* silent */ } finally { setLoading(false); }
    })();
  }, [projectId]);

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-2 border-slate-300 border-t-slate-800 rounded-full" /></div>;
  if (!data) return null;

  const { kpis, deadline_radar, risk_blockers, matters_by_status, recent_matters } = data;

  return (
    <div className="space-y-5" data-testid="dispute-dashboard">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-barlow font-bold uppercase tracking-tight">Dispute Dashboard</h2>
        {kpis.total_amount > 0 && (
          <Badge className="bg-slate-900 text-white text-xs">Total Exposure: {new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR', maximumFractionDigits: 0 }).format(kpis.total_amount)}</Badge>
        )}
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-9 gap-2" data-testid="dispute-kpis">
        {KPI_CONFIG.map(kpi => {
          const Icon = kpi.icon;
          const val = kpis[kpi.key] || 0;
          return (
            <Card key={kpi.key} className="hover:shadow-sm transition-shadow">
              <CardContent className="p-2.5 text-center">
                <Icon className={`h-4 w-4 mx-auto mb-1 ${kpi.color}`} />
                <p className={`text-xl font-bold ${kpi.color}`}>{val}</p>
                <p className="text-[8px] text-slate-500 uppercase leading-tight mt-0.5">{kpi.label}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Deadline Radar */}
        <Card>
          <CardContent className="p-4">
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3 flex items-center gap-1.5">
              <Calendar className="h-3.5 w-3.5" /> Deadline Radar
            </h3>
            <div className="space-y-2">
              {[
                { label: 'Missed', items: deadline_radar.missed, color: 'bg-red-500' },
                { label: 'Next 7 days', items: deadline_radar.next_7, color: 'bg-amber-500' },
                { label: 'Next 14 days', items: deadline_radar.next_14, color: 'bg-blue-500' },
                { label: 'Next 30 days', items: deadline_radar.next_30, color: 'bg-slate-400' },
              ].map(g => (
                <div key={g.label} className="flex items-center justify-between py-1">
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${g.color}`} />
                    <span className="text-sm text-slate-700">{g.label}</span>
                  </div>
                  <span className={`font-bold text-sm ${g.items?.length > 0 ? 'text-slate-900' : 'text-slate-300'}`}>{g.items?.length || 0}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Risk / Blocker Panel */}
        <Card>
          <CardContent className="p-4">
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3 flex items-center gap-1.5">
              <AlertTriangle className="h-3.5 w-3.5" /> Risk / Blockers
            </h3>
            {risk_blockers.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-3">No active risks</p>
            ) : (
              <div className="space-y-1.5 max-h-[180px] overflow-y-auto">
                {risk_blockers.map((r, i) => (
                  <div key={i} className="flex items-center gap-2 p-1.5 rounded bg-red-50 border border-red-100 cursor-pointer hover:bg-red-100" onClick={() => onOpenMatter(r.matter_id)}>
                    <AlertTriangle className="h-3 w-3 text-red-500 shrink-0" />
                    <span className="text-xs text-slate-700 flex-1">{r.matter_ref}</span>
                    <span className="text-[10px] text-red-600">{RISK_LABELS[r.risk] || r.risk}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Matters by Status */}
        <Card>
          <CardContent className="p-4">
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3 flex items-center gap-1.5">
              <TrendingUp className="h-3.5 w-3.5" /> Matters by Status
            </h3>
            <div className="space-y-1.5">
              {Object.entries(matters_by_status).filter(([, v]) => v > 0).map(([status, count]) => (
                <div key={status} className="flex items-center justify-between py-0.5">
                  <Badge className={`text-[10px] ${STATUS_COLORS[status] || 'bg-slate-100'}`}>{status.replace(/_/g, ' ')}</Badge>
                  <span className="font-bold text-sm">{count}</span>
                </div>
              ))}
              {Object.values(matters_by_status).every(v => v === 0) && (
                <p className="text-sm text-slate-400 text-center py-3">No matters yet</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Matters */}
      {recent_matters.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Recent Matters</h3>
            <div className="space-y-2">
              {recent_matters.map(m => (
                <div key={m.id} className="flex items-center gap-3 p-2.5 rounded border hover:bg-slate-50 cursor-pointer" onClick={() => onOpenMatter(m.id)} data-testid={`recent-matter-${m.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs text-slate-400">{m.matter_ref}</span>
                      <h4 className="text-sm font-medium text-slate-800 truncate">{m.title}</h4>
                    </div>
                    <div className="flex items-center gap-3 mt-0.5 text-xs text-slate-500">
                      {m.counterparty && <span>{m.counterparty}</span>}
                      {m.amount_in_dispute > 0 && <span>{new Intl.NumberFormat('en-ZA', { style: 'currency', currency: m.currency || 'ZAR', maximumFractionDigits: 0 }).format(m.amount_in_dispute)}</span>}
                    </div>
                  </div>
                  <Badge className={`text-[10px] ${STATUS_COLORS[m.status] || 'bg-slate-100'}`}>{m.status?.replace(/_/g, ' ')}</Badge>
                  {m.risk_rating === 'critical' && <Badge className="bg-red-600 text-white text-[9px]">Critical</Badge>}
                  {m.risk_rating === 'high' && <Badge className="bg-red-100 text-red-700 text-[9px]">High</Badge>}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
